import { isAxiosError, type AxiosResponse } from "axios";
import { ErrorCode } from "~/api/generated/model";

const LEGACY_MESSAGE_CODES: Record<string, ErrorCode> = {
  "Phone number already in use": ErrorCode.PHONE_NUMBER_IN_USE,
  "Invalid phone number": ErrorCode.INVALID_PHONE_NUMBER,
};

const codeForStatus = (status: number): ErrorCode => {
  switch (status) {
    case 401:
      return ErrorCode.UNAUTHORIZED;
    case 403:
      return ErrorCode.FORBIDDEN;
    case 404:
      return ErrorCode.NOT_FOUND;
    case 409:
      return ErrorCode.CONFLICT;
    case 423:
      return ErrorCode.LOCKED;
    case 424:
      return ErrorCode.FAILED_DEPENDENCY;
    case 429:
      return ErrorCode.TOO_MANY_REQUESTS;
    case 502:
    case 503:
    case 504:
      return ErrorCode.UPSTREAM_UNAVAILABLE;
    default:
      return status >= 400 && status < 500
        ? ErrorCode.VALIDATION
        : ErrorCode.INTERNAL;
  }
};

const field = (data: unknown, key: string): unknown =>
  typeof data === "object" && data !== null
    ? Reflect.get(data, key)
    : undefined;

const isErrorCode = (value: unknown): value is ErrorCode =>
  Object.values<unknown>(ErrorCode).includes(value);

const codeFromBackendWithoutErrorCode = (
  response: AxiosResponse,
): ErrorCode => {
  if (
    response.status === 403 &&
    response.headers?.["x-impersonation-denied"] === "true"
  ) {
    return ErrorCode.IMPERSONATION_DENIED;
  }
  const message = field(response.data, "message");
  const byMessage =
    typeof message === "string" ? LEGACY_MESSAGE_CODES[message] : undefined;
  return byMessage ?? codeForStatus(response.status);
};

export const errorCodeOf = (error: unknown): ErrorCode | undefined => {
  if (!isAxiosError(error) || !error.response) return undefined;
  const code = field(error.response.data, "code");
  return isErrorCode(code)
    ? code
    : codeFromBackendWithoutErrorCode(error.response);
};

export const hasErrorCode = (error: unknown, code: ErrorCode): boolean =>
  errorCodeOf(error) === code;
